"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosAgentCreditLedgerRowSchema = exports.APosAgentCreditLedgerUsageBilledSchema = exports.APosAgentCreditLedgerAllowanceGrantSchema = exports.APosAgentCreditLedgerAdminDeductSchema = exports.APosAgentCreditLedgerAdminGrantSchema = exports.APosAgentCreditLedgerRefundSchema = exports.APosAgentCreditLedgerConsumeSchema = exports.APosAgentCreditLedgerPurchaseSchema = exports.APosAgentCreditBalanceRowSchema = exports.APosAgentCreditIdempotencyEntrySchema = exports.APosAgentCreditReservationSchema = exports.APosAgentCreditBreakdownEntrySchema = exports.APosAgentCreditBreakdownDetailSchema = exports.APosAgentCreditApiKeySchema = exports.APosAgentCreditOperationSchema = exports.APosAgentCreditTransactionTypeSchema = void 0;
const zod_1 = require("zod");
const id_schema_1 = require("../../common/id.schema");
const ai_registry_schema_1 = require("../platform/ai-registry.schema");
/**
 * AgenticPOS AI Agent Credits — Entity Schemas
 *
 * Shape of rows stored in DynamoDB `APosAgentCreditsTable`.
 * Covers both the singleton per-tenant BALANCE row and the
 * append-only CONSUME / PURCHASE / REFUND / ADMIN_GRANT /
 * ADMIN_DEDUCT ledger rows.
 *
 * See docs/superpowers/specs/2026-04-10-ai-product-assist-design.md §7.1
 */
// ---------- Primitive enums / unions ----------
exports.APosAgentCreditTransactionTypeSchema = zod_1.z.enum([
    'PURCHASE',
    'CONSUME',
    'REFUND',
    'ADMIN_GRANT',
    'ADMIN_DEDUCT',
    // --- 2026-09-27, Ruby AI Actions: monthly tier allowance grant (additive) ---
    'ALLOWANCE_GRANT',
    // --- 2026-10-11, additional usage: unbilled overage moved onto an invoice (additive) ---
    'USAGE_BILLED',
]);
/**
 * Billable operations.
 *
 * ADDITIVE ONLY. A value may be appended; none may be removed, renamed or
 * reordered — historical ledger rows carry these strings and the CI
 * breaking-change checker rejects a narrowing.
 *
 * The last five were added 2026-09-01 so that every AI call site metered by
 * @platform/ai-router has an operation to book against
 * (spec 2026-09-01-ai-control-center-design.md section 5).
 */
exports.APosAgentCreditOperationSchema = zod_1.z.enum([
    'analytics_query',
    'product_assist',
    'description_only',
    'image_search_only',
    'inventory_forecast',
    // --- 2026-09-01, W8 one-meter ---
    'mcp_tool_call',
    'word_art',
    'menu_board',
    'image_generation',
    'video_generation',
    // --- 2026-10-04, Ruby Generation Engine (additive) ---
    'ruby_generation',
    // --- 2026-10-08, careers job description (additive) ---
    'job_description',
    // --- 2026-10-11, Ruby Estate property hub (additive) ---
    'estate_lookup',
    'estate_valuation',
    'estate_rent',
    'estate_analysis',
    // --- 2026-10-15, Ruby Estate property insights (additive) ---
    'estate_insights',
    // --- 2026-10-13, Ruby Social Publishing (additive) ---
    'social_caption',
    'social_publish',
    // --- 2026-10-15, Ruby Identity verification (additive) ---
    'identity_verification',
    // --- 2026-10-17, Document Management storage meter (additive) ---
    'document_storage',
    // --- 2026-10-17, Document Management — Ruby reads the invoice (additive) ---
    'document_ruby',
]);
exports.APosAgentCreditApiKeySchema = zod_1.z.enum([
    'anthropic',
    'google_vertex',
    'google_cse',
]);
// ---------- Breakdown entry (per-API line within a CONSUME row) ----------
exports.APosAgentCreditBreakdownDetailSchema = zod_1.z
    .object({
    // anthropic + google_vertex description + google_cse grounding
    input_tokens: zod_1.z.number().int().nonnegative().optional(),
    output_tokens: zod_1.z.number().int().nonnegative().optional(),
    // google_vertex (vision|description) + google_cse grounding (post-Wave-1.6)
    type: zod_1.z.enum(['vision', 'description', 'grounding']).optional(),
    visionCalls: zod_1.z.number().int().nonnegative().optional(),
    // google_cse: legacy (CSE JSON API) and Wave 1.6+ (grounding)
    // `calls` is kept for forward compat; `grounding_calls` is the
    // new explicit name used by the Gemini grounding flow introduced
    // in Wave 1.6. Both are optional and additive so older ledger
    // rows continue to parse.
    calls: zod_1.z.number().int().nonnegative().optional(),
    grounding_calls: zod_1.z.number().int().nonnegative().optional(),
})
    .passthrough();
exports.APosAgentCreditBreakdownEntrySchema = zod_1.z.object({
    api: exports.APosAgentCreditApiKeySchema,
    costUsd: zod_1.z.number().nonnegative(),
    burnRate: zod_1.z.number().nonnegative(),
    credits: zod_1.z.number().int().nonnegative(),
    detail: exports.APosAgentCreditBreakdownDetailSchema.optional(),
});
// ---------- Balance row (one per tenant) ----------
exports.APosAgentCreditReservationSchema = zod_1.z.object({
    ts: zod_1.z.string(),
    operation: exports.APosAgentCreditOperationSchema,
    credits: zod_1.z.number().int().nonnegative(),
    expiresAt: zod_1.z.string(),
    // --- 2026-09-27, Ruby Token wallet buckets (spec §4). Optional, additive.
    // A reservation written before buckets existed drew only from `balance`
    // (the purchased bucket), so an absent split reads as all-purchased.
    /** Credits of this reservation drawn from the tier allowance. */
    fromAllowance: zod_1.z.number().int().nonnegative().optional(),
    /** Credits of this reservation drawn from the purchased balance. */
    fromPurchased: zod_1.z.number().int().nonnegative().optional(),
    /** Allowance period the allowance part belongs to (refunds only return to the same period). */
    allowancePeriodEnd: zod_1.z.string().optional(),
    // --- 2026-10-11, Additional usage (owner: billing beyond tier allowance). Optional, additive.
    fromOverage: zod_1.z.number().nonnegative().optional(),
});
exports.APosAgentCreditIdempotencyEntrySchema = zod_1.z.object({
    ts: zod_1.z.string(),
    operation: zod_1.z.string(),
    reservedCredits: zod_1.z.number().int().nonnegative(),
    status: zod_1.z.enum(['reserved', 'committed', 'refunded']),
    reservationId: zod_1.z.string().optional(),
});
exports.APosAgentCreditBalanceRowSchema = zod_1.z.object({
    pk: id_schema_1.TenantIdSchema, // tenantId
    sk: zod_1.z.literal('BALANCE'),
    balance: zod_1.z.number().nonnegative(),
    totalPurchased: zod_1.z.number().nonnegative().default(0),
    totalGranted: zod_1.z.number().nonnegative().default(0),
    totalConsumed: zod_1.z.number().nonnegative().default(0),
    totalRefunded: zod_1.z.number().nonnegative().default(0),
    totalDeducted: zod_1.z.number().nonnegative().default(0),
    lastTransactionAt: zod_1.z.string().optional(),
    idempotency: zod_1.z
        .record(zod_1.z.string(), exports.APosAgentCreditIdempotencyEntrySchema)
        .default({}),
    reservations: zod_1.z
        .record(zod_1.z.string(), exports.APosAgentCreditReservationSchema)
        .default({}),
    createdAt: zod_1.z.string().optional(),
    updatedAt: zod_1.z.string().optional(),
    // --- 2026-09-27, Ruby AI Actions wallet (spec §4). All optional, additive.
    // `balance` above IS the purchased bucket (never expires); the allowance is
    // a second bucket granted per renewal that expires at the next one.
    // A missing allowance reads as 0 allowance — never as a block.
    /** Monthly tier allowance remaining, in credits. Reads as 0 past allowancePeriodEnd. */
    allowanceBalance: zod_1.z.number().nonnegative().optional(),
    /** ISO end of the allowance period (the next renewal). */
    allowancePeriodEnd: zod_1.z.string().optional(),
    /** Idempotency key of the last grant (the subscription period key). */
    allowanceGrantedFor: zod_1.z.string().optional(),
    /** Credits granted for the current period (for "% left this month"). */
    allowanceIncluded: zod_1.z.number().nonnegative().optional(),
    /** Tier package the allowance was granted for (e.g. pkg_ai_pro). */
    allowanceTierId: zod_1.z.string().optional(),
    /** Opt-in auto top-up. Absent = false (D8). */
    autoTopUp: zod_1.z.boolean().optional(),
    /** Additional usage limit in credits for the unbilled period. null = no limit. Absent = DEFAULT on first enable. */
    overageCap: zod_1.z.number().nonnegative().nullable().optional(),
    /** Credits spent beyond allowance + purchased, not yet on an invoice. Includes credits still
     * reserved-but-uncommitted, so it can never be invoiced ahead of the commit that earns them. */
    overageUsed: zod_1.z.number().nonnegative().optional(),
    /** Committed overage not yet invoiced (subset of `overageUsed` — reservations excluded).
     * `takeUnbilledUsage` only ever draws from this counter. Additive, 2026-10-11 fix round 1. */
    overageBillable: zod_1.z.number().nonnegative().optional(),
    /** Lifetime credits billed as additional usage. */
    overageBilledTotal: zod_1.z.number().nonnegative().optional(),
    autoTopUpConsent: zod_1.z.object({ at: zod_1.z.string(), userId: zod_1.z.string(), textVersion: zod_1.z.string() }).optional(),
    /** allowancePeriodEnd the "allowance used up" notice was sent for. */
    overageStartedNoticeFor: zod_1.z.string().optional(),
    /** allowancePeriodEnd the "limit reached" notice was sent for. */
    overageCapNoticeFor: zod_1.z.string().optional(),
    /** Last vibe the tenant generated with (any action). */
    lastUsedVibeId: zod_1.z.string().optional(),
    /** Last vibe per action id. */
    lastUsedVibeByAction: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).optional(),
});
// ---------- Ledger rows (discriminated union on `type`) ----------
const APosAgentCreditLedgerBaseSchema = zod_1.z.object({
    pk: id_schema_1.TenantIdSchema,
    sk: zod_1.z.string(), // TXN#<ISO>#<ulid>
    gsi1pk: zod_1.z.string(), // TXN#<type>
    gsi1sk: zod_1.z.string(), // ISO timestamp
    creditsAmount: zod_1.z.number(), // signed
    balanceBefore: zod_1.z.number().nonnegative(),
    balanceAfter: zod_1.z.number().nonnegative(),
    createdAt: zod_1.z.string(),
    requestId: zod_1.z.string(),
    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    // --- 2026-09-27, Ruby AI Actions (optional, additive) ---
    /** Which bucket this row moved. Absent on legacy rows = purchased. */
    bucket: ai_registry_schema_1.AiSpendBucketSchema.optional(),
    allowanceCredits: zod_1.z.number().nonnegative().optional(),
    purchasedCredits: zod_1.z.number().nonnegative().optional(),
    /** Additional-usage (overage) credits moved by this row. Additive, 2026-10-11. */
    overageCredits: zod_1.z.number().nonnegative().optional(),
    actionId: zod_1.z.string().optional(),
    vibeId: zod_1.z.string().optional(),
});
exports.APosAgentCreditLedgerPurchaseSchema = APosAgentCreditLedgerBaseSchema.extend({
    type: zod_1.z.literal('PURCHASE'),
    stripePaymentIntentId: zod_1.z.string(),
    stripeCheckoutSessionId: zod_1.z.string().optional(),
    stripeBundleId: zod_1.z.string().optional(),
    bundleLabel: zod_1.z.string().optional(),
    bundleQuantity: zod_1.z.number().int().positive().optional(),
    creditsPerUnit: zod_1.z.number().int().positive().optional(),
});
exports.APosAgentCreditLedgerConsumeSchema = APosAgentCreditLedgerBaseSchema.extend({
    type: zod_1.z.literal('CONSUME'),
    operation: exports.APosAgentCreditOperationSchema,
    costUsd: zod_1.z.number().nonnegative(),
    breakdown: zod_1.z.array(exports.APosAgentCreditBreakdownEntrySchema),
    reservationId: zod_1.z.string().optional(),
});
exports.APosAgentCreditLedgerRefundSchema = APosAgentCreditLedgerBaseSchema.extend({
    type: zod_1.z.literal('REFUND'),
    refundOfTxnId: zod_1.z.string().optional(),
    reservationId: zod_1.z.string().optional(),
    reason: zod_1.z.string().optional(),
});
exports.APosAgentCreditLedgerAdminGrantSchema = APosAgentCreditLedgerBaseSchema.extend({
    type: zod_1.z.literal('ADMIN_GRANT'),
    grantedBy: zod_1.z.string(),
    reason: zod_1.z.string().optional(),
});
exports.APosAgentCreditLedgerAdminDeductSchema = APosAgentCreditLedgerBaseSchema.extend({
    type: zod_1.z.literal('ADMIN_DEDUCT'),
    grantedBy: zod_1.z.string(),
    reason: zod_1.z.string().optional(),
});
/** Monthly tier allowance granted on renewal. Idempotent on `periodKey`. */
exports.APosAgentCreditLedgerAllowanceGrantSchema = APosAgentCreditLedgerBaseSchema.extend({
    type: zod_1.z.literal('ALLOWANCE_GRANT'),
    periodKey: zod_1.z.string(),
    periodEnd: zod_1.z.string(),
    tierPackageId: zod_1.z.string().optional(),
    invoiceId: zod_1.z.string().optional(),
    allowanceBefore: zod_1.z.number().nonnegative().optional(),
    allowanceAfter: zod_1.z.number().nonnegative().optional(),
});
/**
 * Additional-usage credits moved from the unbilled bucket onto an invoice.
 * Idempotent on invoiceId (sk = `USAGEBILL#<invoiceId>`). Deliberately its
 * OWN base — not `APosAgentCreditLedgerBaseSchema` — because this row has no
 * balance effect to report: `overageUsed`/`overageBillable` move, `balance`
 * does not, so `balanceBefore`/`balanceAfter` are never populated here.
 *
 * `balanceBefore`/`balanceAfter` are declared optional (fix round 2,
 * 2026-10-11) purely so generic ledger readers (history/export handlers)
 * that are typed against the `APosAgentCreditLedgerRow` union can read
 * `row.balanceBefore`/`row.balanceAfter` without a type-narrowing branch per
 * row type — they stay `undefined` on every USAGE_BILLED row; writers never
 * set them.
 */
exports.APosAgentCreditLedgerUsageBilledSchema = zod_1.z.object({
    pk: id_schema_1.TenantIdSchema,
    sk: zod_1.z.string(), // USAGEBILL#<invoiceId>
    gsi1pk: zod_1.z.string(), // TXN#USAGE_BILLED
    gsi1sk: zod_1.z.string(), // ISO timestamp
    type: zod_1.z.literal('USAGE_BILLED'),
    creditsAmount: zod_1.z.number().nonnegative(),
    invoiceId: zod_1.z.string(),
    requestId: zod_1.z.string().optional(),
    createdAt: zod_1.z.string(),
    balanceBefore: zod_1.z.number().optional(),
    balanceAfter: zod_1.z.number().optional(),
});
exports.APosAgentCreditLedgerRowSchema = zod_1.z.discriminatedUnion('type', [
    exports.APosAgentCreditLedgerPurchaseSchema,
    exports.APosAgentCreditLedgerConsumeSchema,
    exports.APosAgentCreditLedgerRefundSchema,
    exports.APosAgentCreditLedgerAdminGrantSchema,
    exports.APosAgentCreditLedgerAdminDeductSchema,
    exports.APosAgentCreditLedgerAllowanceGrantSchema,
    exports.APosAgentCreditLedgerUsageBilledSchema,
]);
//# sourceMappingURL=agent-credits.schema.js.map